// ─── 🎓 Il «Listino prezzi lezioni»: quanto paga un allievo ──────────────────
//
// 🗣️ Voce 19, tappa 19b (24/09/2026), dal mockup approvato
// (`mockup/listino-lezioni-mockup.html`). Sue parole: *«io ho bisogno, anziché
// inserire le persone che pagano, di inserire le tariffe dei tipi di lezione che
// effettuano i soci»*.
//
// ⭐ **Non è una griglia di orari come gli altri due listini: è una cifra per
//    tipo.** Tre tabelle, una colonna ciascuna, e la durata la dice il tipo:
//
// | tipo | righe | durata | la cifra |
// |---|---|---|---|
// | lezione | 1 · 2 · 3 · 4 persone | 1 ora | a testa |
// | allenamento | 🔄 4 persone (20/10/2026) | 1 ora e mezza | a testa |
// | corso | principiante · intermedio · avanzato | 1 ora | al mese, a persona |
// | 🆕 tattica | una riga per maestro, 3 persone | 1 ora e mezza | a testa |
//
// 🆕 20/10/2026 (mockup «Partita tattica» v2, *«approvato, procedi»*): 🗣️ *«partita
// tattica ed è una partita con 3 persone + il maestro»* · *«gli allenamenti sono a
// 4 persone»* · *«differenziare il prezzo a seconda del maestro che la fa»*.
// ⚖️ Le righe dell'allenamento da 1 a 3 scritte prima restano nel database (il
// CHECK non si stringe), ma non si mostrano, non si scrivono e non prezzano più.
//
// 🗣️ Le tre durate e i livelli sono parole sue, dette guardando il mockup:
// *«Nella tabella delle lezioni lasciami solo un'ora»* · *«allenamenti […]
// uguale a lezioni, solo un'ora e mezza»* · *«i corsi sono mensili quindi non
// servono più gli appuntamenti»* · *«le tipologie di corso principiante,
// intermedio, avanzato e per ognuno di questi poter inserire un costo a
// persona»*.
//
// 🔒 **Una casella vuota è una riga che NON C'È**, come nella griglia dei
//    listini: «prezzo non deciso» e «gratis» (zero) non si confondono per forma.
// ✍️ **Si scrive solo dal gestionale**: 🗣️ *«L'unica cosa che dall'app maestri
//    non si deve fare è mettere l'importo alle varie tipologie di lezione o di
//    corso.»*
import { z } from 'zod';
export const TIPI_TARIFFA = ['lezione', 'allenamento', 'corso', 'tattica'];
export const tipoTariffaSchema = z.enum(TIPI_TARIFFA);
export const LIVELLI_CORSO = ['principiante', 'intermedio', 'avanzato'];
export const livelloCorsoSchema = z.enum(LIVELLI_CORSO);
/** Quante persone al massimo in una lezione. */
export const PERSONE_MASSIME_LEZIONE = 4;
/** 🔄 20/10/2026: un allenamento è sempre da quattro. */
export const PERSONE_ALLENAMENTO = 4;
/** 🆕 20/10/2026: una partita tattica è da tre, più il maestro. */
export const PERSONE_TATTICA = 3;
/**
 * ⏱️ **La durata la dice il tipo, e non si scrive nella riga.** Una lezione da
 * un'ora e mezza non esiste più: è un allenamento. Scriverla anche nel
 * database vorrebbe dire poter salvare una combinazione che nessuno ha chiesto.
 */
export const DURATA_DEL_TIPO = {
    lezione: 60,
    allenamento: 90,
    corso: 60,
    tattica: 90,
};
export const ETICHETTE_TIPO_TARIFFA = {
    lezione: 'Lezioni',
    allenamento: 'Allenamenti',
    corso: 'Corsi',
    tattica: 'Partita tattica',
};
export const ETICHETTE_LIVELLO = {
    principiante: 'Principiante',
    intermedio: 'Intermedio',
    avanzato: 'Avanzato',
};
/**
 * La forma giusta di una casella **da scrivere**: livello per il corso, quattro
 * per l'allenamento, tre e il maestro per la partita tattica, da 1 a 4 per la lezione.
 */
export function voceBenFatta(v) {
    const maestro = v.instructorId ?? null;
    if (v.kind === 'tattica')
        return v.level === null && v.people === PERSONE_TATTICA && maestro !== null;
    if (maestro !== null)
        return false;
    if (v.kind === 'corso')
        return v.level !== null && v.people === null;
    if (v.kind === 'allenamento')
        return v.level === null && v.people === PERSONE_ALLENAMENTO;
    return v.level === null && v.people !== null && v.people >= 1 && v.people <= PERSONE_MASSIME_LEZIONE;
}
export const tariffaLezioneSchema = z.object({
    kind: tipoTariffaSchema,
    people: z.number().int().nullable(),
    level: livelloCorsoSchema.nullable(),
    /** 🆕 Il maestro della partita tattica; `null` per gli altri tipi. */
    instructorId: z.string().uuid().nullable().default(null),
    /** A testa (lezione, allenamento, partita tattica) o al mese a persona (corso). */
    priceCents: z.number().int().min(0),
});
export const tariffeLezioniSchema = z.object({ items: z.array(tariffaLezioneSchema) });
/**
 * ✍️ Scrivere una casella. `priceCents: null` la **svuota** — cioè toglie la
 * riga, e il prezzo torna «non deciso».
 */
export const scriviTariffaSchema = z
    .object({
    kind: tipoTariffaSchema,
    people: z.number().int().nullable().default(null),
    level: livelloCorsoSchema.nullable().default(null),
    instructorId: z.string().uuid().nullable().default(null),
    priceCents: z.number().int().min(0).max(1_000_000).nullable(),
})
    .refine(voceBenFatta, {
    message: 'Una lezione vuole da 1 a 4 persone, un allenamento 4, una partita tattica 3 e il maestro, un corso il livello',
});
/**
 * Le caselle di una tabella, nell'ordine in cui si mostrano. 🆕 La partita
 * tattica ne ha una per maestro: chi chiama dà i maestri (gli id, nell'ordine).
 */
export function vociDelTipo(kind, maestri = []) {
    if (kind === 'corso')
        return LIVELLI_CORSO.map((level) => ({ kind, people: null, level }));
    if (kind === 'allenamento')
        return [{ kind, people: PERSONE_ALLENAMENTO, level: null }];
    if (kind === 'tattica')
        return maestri.map((instructorId) => ({ kind, people: PERSONE_TATTICA, level: null, instructorId }));
    return Array.from({ length: PERSONE_MASSIME_LEZIONE }, (_, i) => ({ kind, people: i + 1, level: null }));
}
/** La stessa casella? Serve a trovarla fra le righe lette. */
export function stessaVoce(a, b) {
    return (a.kind === b.kind &&
        a.people === b.people &&
        a.level === b.level &&
        (a.instructorId ?? null) === (b.instructorId ?? null));
}
/** Il prezzo di una casella, o `null` se non è deciso. */
export function prezzoDellaVoce(tariffe, v) {
    return tariffe.find((t) => stessaVoce(t, v))?.priceCents ?? null;
}
// ─── 🆕 Voce 19c: il listino lezioni DENTRO LA PRENOTAZIONE ──────────────────
//
// 🗣️ Sua domanda, il 24/09/2026: *«quando vado a inserire un giocatore mi riporta
// i prezzi che ho messo dentro le varie sezioni del gestionale?»* — e non lo
// faceva: l'importo veniva dal listino dei giocatori. ⇒ Queste funzioni sono il
// ponte fra una prenotazione e la sua casella, e stanno qui (pure, senza
// database) perché le usano **tre** posti che devono dire la stessa cifra: il
// server che scrive l'importo, il pannello che lo mostra prima di «Crea», e il
// finto server dell'anteprima.
/** Le prenotazioni che si prezzano col «Listino prezzi lezioni». */
export function siPrezzaColListinoLezioni(tipo) {
    return TIPI_TARIFFA.includes(tipo);
}
/**
 * 🔑 La casella che vale per una prenotazione: per la lezione **quanti sono in
 * campo**, per il corso **il livello**. 🔄 L'allenamento è sempre la casella da
 * quattro, la partita tattica sempre quella da tre **del suo maestro**. `null`
 * quando non ce n'è una — nessuno in campo, più di quattro, un corso senza
 * livello, una partita tattica senza maestro.
 */
export function vocePerPrenotazione(tipo, quanti, livello, maestro = null) {
    if (tipo === 'corso')
        return livello === null ? null : { kind: 'corso', people: null, level: livello };
    if (quanti < 1 || quanti > PERSONE_MASSIME_LEZIONE)
        return null;
    if (tipo === 'allenamento')
        return { kind: tipo, people: PERSONE_ALLENAMENTO, level: null };
    if (tipo === 'tattica') {
        if (maestro === null || quanti > PERSONE_TATTICA)
            return null;
        return { kind: tipo, people: PERSONE_TATTICA, level: null, instructorId: maestro };
    }
    return { kind: tipo, people: quanti, level: null };
}
const NOMI_PERSONE = ['singola', 'doppia', 'a tre', 'a quattro'];
const NOME_DURATA = { 60: '1 ora', 90: '1 ora e mezza' };
/**
 * «Lezione doppia, 1 ora» · «Allenamento a tre, 1 ora e mezza» · «Corso
 * principiante». ⚖️ Il nome si **accorda**: una lezione è doppia, un
 * allenamento è doppio.
 */
export function laVoceDetta(v) {
    if (v.level !== null)
        return `Corso ${ETICHETTE_LIVELLO[v.level].toLowerCase()}`;
    if (v.kind === 'tattica')
        return `Partita tattica con il maestro, ${NOME_DURATA[DURATA_DEL_TIPO.tattica]}`;
    const persone = NOMI_PERSONE[(v.people ?? 1) - 1] ?? `da ${v.people}`;
    const nome = v.kind === 'allenamento' ? `Allenamento ${persone.replace(/a$/, 'o')}` : `Lezione ${persone}`;
    return `${nome}, ${NOME_DURATA[DURATA_DEL_TIPO[v.kind]]}`;
}
function euroScritti(cents) {
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}
/**
 * 💶 Il prezzo di una prenotazione secondo il listino, **con la frase che lo
 * dice**. ⛔ Una casella vuota è `null` e lo scrive — mai uno zero che sembra
 * una risposta.
 */
export function prezzoDaListinoLezioni(tariffe, tipo, quanti, livello, maestro = null) {
    const voce = vocePerPrenotazione(tipo, quanti, livello, maestro);
    if (voce === null) {
        if (tipo === 'tattica' && quanti >= 1) {
            return maestro === null
                ? {
                    priceCents: null,
                    perche: 'Il prezzo della partita tattica è quello del suo maestro: si vede quando c’è.',
                }
                : {
                    priceCents: null,
                    perche: `Una partita tattica è da ${PERSONE_TATTICA}: con ${quanti} il prezzo non c’è.`,
                };
        }
        if (tipo === 'corso') {
            return { priceCents: null, perche: 'Questo corso non ha un livello: la quota non è decisa.' };
        }
        if (quanti < 1) {
            return {
                priceCents: null,
                perche: 'Il prezzo lo dà il Listino prezzi lezioni, secondo quanti sono: si vede quando entra qualcuno.',
            };
        }
        return {
            priceCents: null,
            perche: `Il Listino prezzi lezioni arriva a ${PERSONE_MASSIME_LEZIONE} persone: con ${quanti} il prezzo non c’è.`,
        };
    }
    const detta = laVoceDetta(voce);
    const prezzo = prezzoDellaVoce(tariffe, voce);
    if (prezzo === null) {
        return {
            priceCents: null,
            perche: `${detta}: ${tipo === 'corso' ? 'quota non decisa' : 'prezzo non deciso'} nel Listino prezzi lezioni.`,
        };
    }
    return {
        priceCents: prezzo,
        perche: `${detta}: ${euroScritti(prezzo)} ${tipo === 'corso' ? 'al mese a persona' : 'a testa'} · dal Listino prezzi lezioni`,
    };
}
/**
 * 📅 **Le date che portano la quota del mese** di un corso: la prima di ogni
 * mese, e nessun'altra.
 *
 * 🗣️ Decisione sua (24/09/2026): la quota *«sulla prima del mese»*, le altre a
 * zero — si paga una volta, alla prima lezione del mese. E un mese conta se ci
 * cade **almeno una** volta del corso: un mese da cinque mercoledì paga come
 * uno da quattro.
 * ⚠️ Riceve date `AAAA-MM-GG` in qualunque ordine; chi chiama passa **solo le
 * volte che si fanno** (non le annullate), così se la prima si annulla la
 * quota passa da sé alla seconda.
 */
export function primeDelMese(date) {
    const prime = new Map();
    for (const d of date) {
        const mese = d.slice(0, 7);
        const gia = prime.get(mese);
        if (gia === undefined || d < gia)
            prime.set(mese, d);
    }
    return new Set(prime.values());
}
/** Quanti mesi tocca un corso: quelli in cui cade almeno una volta. */
export function mesiDelCorso(date) {
    return primeDelMese(date).size;
}
//# sourceMappingURL=tariffe-lezioni.js.map